/**
 * useBilling Hook
 * React hook for managing billing, subscriptions, and payment methods
 * Integrates with Auth0 for authenticated API calls
 */

import { useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';
import { useAuth0 } from '@auth0/auth0-react';
import {
  getE2EBillingFixture,
  isE2ECommandFixtureEnabled,
} from '../config/e2eCommandFixture';
import billingService from '../services/billingService';

const PACKAGE_NONE = 'none';
const PACKAGE_RELY_MEMBERSHIP = 'rely_membership';

const normalizePackageKey = (packageKey) => {
  switch (packageKey) {
    case PACKAGE_RELY_MEMBERSHIP:
    case 'pro':
    case 'essential':
    case 'business':
    case 'essential_plus':
    case 'business_plus':
      return PACKAGE_RELY_MEMBERSHIP;
    case PACKAGE_NONE:
    case 'free':
    default:
      return PACKAGE_NONE;
  }
};

// Plan pricing for display
export const PLAN_DETAILS = {
  none: {
    name: 'No recurring subscription',
    price: 0,
    priceDisplay: '',
    period: '',
    features: [],
  },
  rely_membership: {
    name: 'Rely Command',
    price: null,
    priceDisplay: '',
    period: 'month',
    features: [
      'Invoices and proposals',
      'Customer records',
      'Billing visibility',
      'Operating overview',
    ],
  },
};

/**
 * Custom hook for billing management
 * @returns {Object} Billing state and actions
 */
export const useBilling = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const isCommandFixtureEnabled = isE2ECommandFixtureEnabled();

  // Subscription state
  const [subscription, setSubscription] = useState(() => {
    if (isCommandFixtureEnabled) {
      return getE2EBillingFixture().subscription;
    }

    return {
      planType: PACKAGE_NONE,
      packageKey: PACKAGE_NONE,
      status: 'active',
      currentPeriodEnd: null,
      canceledAt: null,
      stripeSubscriptionId: null,
      entitlements: [],
    };
  });

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState(() =>
    isCommandFixtureEnabled ? getE2EBillingFixture().paymentMethods : []
  );

  // Billing history state
  const [billingHistory, setBillingHistory] = useState(() =>
    isCommandFixtureEnabled ? getE2EBillingFixture().billingHistory : []
  );

  // Loading states
  const [loading, setLoading] = useState({
    subscription: false,
    paymentMethods: false,
    history: false,
    action: false,
  });

  // Error state
  const [error, setError] = useState(null);

  /**
   * Get access token and set it on the service
   */
  const getToken = useCallback(async () => {
    if (!isAuthenticated) return null;
    try {
      const token = await getAccessTokenSilently();
      billingService.setAccessToken(token);
      return token;
    } catch (err) {
      logger.error('Failed to get access token:', err);
      return null;
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  /**
   * Fetch subscription data
   */
  const fetchSubscription = useCallback(async () => {
    if (isCommandFixtureEnabled) {
      const fixture = getE2EBillingFixture().subscription;
      setSubscription(fixture);
      return fixture;
    }

    if (!isAuthenticated) return;

    try {
      setLoading((prev) => ({ ...prev, subscription: true }));
      setError(null);
      await getToken();

      const data = await billingService.getSubscription();
      const packageKey = normalizePackageKey(
        data.package_key || data.plan_type
      );
      const hasRecurringSubscription =
        typeof data.has_recurring_subscription === 'boolean'
          ? data.has_recurring_subscription
          : Boolean(data.stripe_subscription_id) ||
            packageKey === PACKAGE_RELY_MEMBERSHIP;
      setSubscription({
        planType: packageKey,
        packageKey,
        status: data.status || 'active',
        currentPeriodEnd: data.current_period_end,
        canceledAt: data.canceled_at,
        stripeSubscriptionId: data.stripe_subscription_id,
        hasRecurringSubscription,
        entitlements: data.entitlements || [],
      });
    } catch (err) {
      logger.error('Failed to fetch subscription:', err);
      // Don't set error for 404 - just means no subscription yet
      if (err.status !== 404) {
        setError(err.message);
      }
    } finally {
      setLoading((prev) => ({ ...prev, subscription: false }));
    }
  }, [isAuthenticated, getToken, isCommandFixtureEnabled]);

  /**
   * Fetch payment methods
   */
  const fetchPaymentMethods = useCallback(async () => {
    if (isCommandFixtureEnabled) {
      const fixture = getE2EBillingFixture().paymentMethods;
      setPaymentMethods(fixture);
      return fixture;
    }

    if (!isAuthenticated) return;

    try {
      setLoading((prev) => ({ ...prev, paymentMethods: true }));
      await getToken();

      const methods = await billingService.getPaymentMethods();
      setPaymentMethods(methods || []);
    } catch (err) {
      logger.error('Failed to fetch payment methods:', err);
    } finally {
      setLoading((prev) => ({ ...prev, paymentMethods: false }));
    }
  }, [isAuthenticated, getToken, isCommandFixtureEnabled]);

  /**
   * Fetch billing history
   * @param {number} limit - Number of records to fetch
   * @returns {Promise<Array|null>} Billing history records or null on error
   */
  const fetchBillingHistory = useCallback(
    async (limit = 10) => {
      if (isCommandFixtureEnabled) {
        const fixture = getE2EBillingFixture().billingHistory;
        setBillingHistory(fixture);
        return fixture;
      }

      if (!isAuthenticated) return null;

      try {
        setLoading((prev) => ({ ...prev, history: true }));
        await getToken();

        const history = await billingService.getBillingHistory(limit);
        setBillingHistory(history || []);
        return history || [];
      } catch (err) {
        logger.error('Failed to fetch billing history:', err);
        return null;
      } finally {
        setLoading((prev) => ({ ...prev, history: false }));
      }
    },
    [isAuthenticated, getToken, isCommandFixtureEnabled]
  );

  /**
   * Create or upgrade subscription
   * @param {string} planType - recurring package key, currently 'rely_membership'
   */
  const createSubscription = useCallback(
    async (planType) => {
      if (isCommandFixtureEnabled) {
        const fixture = getE2EBillingFixture().subscription;
        setSubscription(fixture);
        return { planType, subscription: fixture };
      }

      try {
        setLoading((prev) => ({ ...prev, action: true }));
        setError(null);
        await getToken();

        const result = await billingService.createSubscription(planType);

        // Refresh subscription data
        await fetchSubscription();

        return result;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading((prev) => ({ ...prev, action: false }));
      }
    },
    [getToken, fetchSubscription, isCommandFixtureEnabled]
  );

  /**
   * Cancel subscription
   */
  const cancelSubscription = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, action: true }));
      setError(null);
      await getToken();

      const result = await billingService.cancelSubscription();

      // Refresh subscription data
      await fetchSubscription();

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  }, [getToken, fetchSubscription]);

  /**
   * Reactivate subscription
   */
  const reactivateSubscription = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, action: true }));
      setError(null);
      await getToken();

      const result = await billingService.reactivateSubscription();

      // Refresh subscription data
      await fetchSubscription();

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  }, [getToken, fetchSubscription]);

  /**
   * Create SetupIntent for adding payment method
   */
  const createSetupIntent = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, action: true }));
      await getToken();

      return await billingService.createSetupIntent();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading((prev) => ({ ...prev, action: false }));
    }
  }, [getToken]);

  /**
   * Add payment method
   * @param {string} stripePaymentMethodId - Stripe PaymentMethod ID
   * @param {boolean} setAsDefault - Whether to set as default
   */
  const addPaymentMethod = useCallback(
    async (stripePaymentMethodId, setAsDefault = true) => {
      try {
        setLoading((prev) => ({ ...prev, action: true }));
        setError(null);
        await getToken();

        const result = await billingService.addPaymentMethod(
          stripePaymentMethodId,
          setAsDefault
        );

        // Refresh payment methods
        await fetchPaymentMethods();

        return result;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading((prev) => ({ ...prev, action: false }));
      }
    },
    [getToken, fetchPaymentMethods]
  );

  /**
   * Remove payment method
   * @param {string} paymentMethodId - Internal payment method ID
   */
  const removePaymentMethod = useCallback(
    async (paymentMethodId) => {
      try {
        setLoading((prev) => ({ ...prev, action: true }));
        setError(null);
        await getToken();

        await billingService.removePaymentMethod(paymentMethodId);

        // Refresh payment methods
        await fetchPaymentMethods();

        return true;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading((prev) => ({ ...prev, action: false }));
      }
    },
    [getToken, fetchPaymentMethods]
  );

  /**
   * Set default payment method
   * @param {string} paymentMethodId - Internal payment method ID
   */
  const setDefaultPaymentMethod = useCallback(
    async (paymentMethodId) => {
      try {
        setLoading((prev) => ({ ...prev, action: true }));
        setError(null);
        await getToken();

        await billingService.setDefaultPaymentMethod(paymentMethodId);

        // Refresh payment methods
        await fetchPaymentMethods();

        return true;
      } catch (err) {
        setError(err.message);
        throw err;
      } finally {
        setLoading((prev) => ({ ...prev, action: false }));
      }
    },
    [getToken, fetchPaymentMethods]
  );

  /**
   * Refresh all billing data
   */
  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchSubscription(),
      fetchPaymentMethods(),
      fetchBillingHistory(),
    ]);
  }, [fetchSubscription, fetchPaymentMethods, fetchBillingHistory]);

  // Initial data fetch when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refreshAll();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get current plan details
  const currentPlanDetails =
    PLAN_DETAILS[subscription.packageKey] || PLAN_DETAILS.none;

  const hasRecurringSubscription =
    typeof subscription.hasRecurringSubscription === 'boolean'
      ? subscription.hasRecurringSubscription
      : subscription.packageKey === PACKAGE_RELY_MEMBERSHIP;

  return {
    // State
    subscription,
    paymentMethods,
    billingHistory,
    loading,
    error,
    currentPlanDetails,

    // Constants
    PLAN_DETAILS,

    // Actions
    fetchSubscription,
    fetchPaymentMethods,
    fetchBillingHistory,
    createSubscription,
    cancelSubscription,
    reactivateSubscription,
    createSetupIntent,
    addPaymentMethod,
    removePaymentMethod,
    setDefaultPaymentMethod,
    refreshAll,

    // Helpers
    isNoRecurringSubscription:
      subscription.packageKey === PACKAGE_NONE && !hasRecurringSubscription,
    isEssentialPlan: false,
    isBusinessPlan: false,
    isPlusPlan: false,
    hasRecurringSubscription,
    isCanceled: subscription.status === 'canceled',
    hasPaymentMethod: paymentMethods.length > 0,
    defaultPaymentMethod:
      paymentMethods.find((pm) => pm.is_default) || paymentMethods[0] || null,
  };
};

export default useBilling;
